import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { useMedia } from 'react-use';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { DotsIcon, LogoutIcon } from 'components/icons/v2';
import { AnimationAsyncButton } from 'components/AnimationButton';
import animationCopy from 'components/AnimationButton/animations/copied.json';
import { useAlerts } from 'app/_context/alertContext';
import { buildLandingUrl } from 'app/(authenticated)/(owner)/landing/utils';
import { useMemberTracking } from 'app/_tracking/events';
import { useTelegramMiniApp } from 'app/_context/TelegramContext';
import { IRouteGroup, IRoutes, MEMBER_ROUTES, OWNER_ROUTES } from 'utils/routes';
import { MemberContext } from 'app/(authenticated)/(member)/_context/Member';
import CommunitySwitcher from 'components/theme-v2/sidebar/CommunitySwitcher';
import { CommunityInfoNode } from 'types';
import { useApplicationsCountQuery, useLogoutUserMutation } from 'generated/hooks';
import { CommunityApplicationStatusChoices, RoleEnum } from 'generated/types';
import { DiscoveryIcon } from 'components/icons/v2/DiscoveryIcon';
import { useTracking } from 'app/_tracking';
import { CurrentUserQuery } from 'generated/operations';
import { Avatar } from 'app/(authenticated)/(member)/_components/Avatar';
import { cn } from 'utils/tw';

type SidebarCurrentUserType = Pick<
  NonNullable<CurrentUserQuery['currentUser']>,
  'id' | 'firstName' | 'lastName' | 'image' | 'communityMember' | 'communityMembers'
>;

interface SidebarProps {
  community?: CommunityInfoNode | null;
  currentUser?: SidebarCurrentUserType | null;
}

const COMMUNITY_ID_NEW_NO_ANALYTICS = 586;

const buildOwnerRoutes = (legacyItems: boolean) => {
  let res: IRouteGroup[] = [
    {
      routeGroupName: 'Community',
      routes: [
        OWNER_ROUTES.SPACE,
        MEMBER_ROUTES.MEMBERS,
        OWNER_ROUTES.DEALS,
        MEMBER_ROUTES.CONNECTIONS,
        OWNER_ROUTES.WIN_WIN
      ]
    }
  ];

  let managementRoutes = [];
  if (legacyItems) {
    managementRoutes.push(OWNER_ROUTES.ANALYTICS);
    managementRoutes.push(OWNER_ROUTES.LANDING_BUILDER);
  }
  managementRoutes.push(OWNER_ROUTES.PANEL);

  res.push({
    routeGroupName: 'Management',
    routes: managementRoutes,
  });

  res.push({
    routeGroupName: 'Settings',
    routes: [
      OWNER_ROUTES.SETTINGS
    ]
  });

  return res;
};

const buildMemberRoutes = (props: SidebarProps) => {
  let communityRoutes = [];

  if ((props.currentUser?.communityMembers?.length || 0) > 0) {
    communityRoutes.push(
      MEMBER_ROUTES.MEMBERS
    );
  }

  communityRoutes.push(
    MEMBER_ROUTES.DEALS,
    MEMBER_ROUTES.CONNECTIONS,
    MEMBER_ROUTES.WIN_WIN
  );

  return [
    {
      routeGroupName: 'Community',
      routes: communityRoutes
    }
  ];
};

export const Sidebar: FC<SidebarProps> = ({ currentUser, community }) => {
  const router = useRouter();
  const pathname = usePathname();
  const isMobile = useMedia('(max-width: 768px)', false);

  const [showLogoutPopup, setShowLogoutPopup] = useState<boolean>(false);
  const { isEnabledTWA } = useTelegramMiniApp();
  const { trackEvent } = useTracking();
  const { trackCopyProfileLink } = useMemberTracking();
  const { error: errorAlert } = useAlerts();
  const { mustHaveProfile } = useContext(MemberContext);

  const [applicationsCountResponse] = useApplicationsCountQuery({variables: { status: CommunityApplicationStatusChoices.New }});
  const [, logoutUser] = useLogoutUserMutation();

  const handleLogout = useCallback(async () => {
    try {
      const response = await logoutUser({});

      if (response.error) {
        return errorAlert(
          'Oops, something went wrong. Please try again later.'
        );
      }

      setShowLogoutPopup(() => false);
      router.push(OWNER_ROUTES.HOME.getPath());
    } catch (e) {
      console.error(e);
    }
  }, [errorAlert, logoutUser, router]);

  const isActive = useCallback(
    (path: string) => {
      return pathname === path;
    },
    [pathname]
  );

  const userProfileLink = useMemo(() => {
    return MEMBER_ROUTES.MEMBER_PROFILE.getPath(currentUser!.id);
  }, [currentUser]);

  const copy = useCallback(async () => {
    try {
      trackCopyProfileLink();

      const copiedText = `${buildLandingUrl(window)}${userProfileLink}`;

      await navigator.clipboard.writeText(copiedText);
    } catch (e) {
      console.error(e);
    }
  }, [userProfileLink, trackCopyProfileLink]);

  const getPath = (route: IRoutes) => {
    switch (route) {
      case OWNER_ROUTES.SPACE:
        return route.getPath(community?.subdomain || community?.domain || '');
      default:
        return route.getPath();
    }
  };

  const displayLegacyMenuItems = useMemo(() => {
    if (!community) return false;
    return parseInt(community.id) < COMMUNITY_ID_NEW_NO_ANALYTICS;
  }, [community]);

  const routeGroups: IRouteGroup[] = useMemo(() => {
    switch (currentUser?.communityMember?.role) {
      case RoleEnum.Owner:
      case RoleEnum.Admin:
        return buildOwnerRoutes(displayLegacyMenuItems);
      case RoleEnum.Member:
      default:
        return buildMemberRoutes({ currentUser, community });
    }
  }, [displayLegacyMenuItems, currentUser?.communityMember?.role]);

  return (
    <div
      className='pt-6 border-r border-gray-100 flex flex-col justify-between flex-1 w-full md:border-r-0 md:h-full bg-gray-50 h-full'>
      <div className='px-3 md:px-[10px] flex flex-col md:gap-0 gap-4 md:flex-1 md:justify-around'>
        {(currentUser?.communityMembers?.length || 0) > 0 ? (
          <div className='w-full'>
            <div className='flex items-center justify-between mb-3'>
              <CommunitySwitcher
                community={community ?? undefined}
                communities={currentUser?.communityMembers?.map((item) => item.community) ?? []}
              />
            </div>
          </div>
        ) : null}
        <div className='flex flex-col gap-[6px]'>
          <div className='flex flex-col gap-6'>
            <Link href={MEMBER_ROUTES.DISCOVERY.getPath()} className={'hidden md:block'} onClick={() => {
              trackEvent({ 'name': 'sidebar_discovery_clicked' });
            }}>
              <Button variant={'nakedBrandLink'} size={'medium'}>
                <DiscoveryIcon />
                Discovery
              </Button>
            </Link>

            {routeGroups.map((routeGroup, index) => (
              <div
                className='flex flex-col gap-[6px]'
                key={index}
              >
                <p className='text-[12px]/[18px] text-gray-600 px-3'>{routeGroup.routeGroupName}</p>
                <div className="flex flex-col gap-1">
                  {routeGroup.routes.map((route, routeIndex) => (
                    <Link href={getPath(route)} key={routeIndex}>
                      <div
                        className={`py-2 px-3 rounded-full hover:bg-gray-100 md:p-5 md:rounded-none ${
                          !routeIndex
                            ? 'md:border-y md:border-y-gray-100'
                            : 'md:border-b border-b-gray-100'
                        } ${isActive(getPath(route)) && 'bg-gray-100'}`}
                      >
                        <div className="flex justify-between">
                          <div className='flex flex-1 gap-2 items-center'>
                            <div className={cn(
                              'flex justify-center w-[20px] h-[20px]',
                              isActive(getPath(route)) ? 'text-brand-500' : 'text-gray-400'
                            )}>
                              {route.icon}
                            </div>
                            <p
                              className={`flex-1 text-medium flex justify-between ${
                                isActive(getPath(route))
                                  ? 'text-black'
                                  : 'text-gray-700'
                              }`}
                            >
                              {route.title}
                              {route === OWNER_ROUTES.PANEL && !!applicationsCountResponse.data?.applications?.count && (
                                <span className="bg-error-100 rounded-full text-error-700 text-small px-[6px] py-[2px] w-6 h-6 flex items-center justify-center">
                                  {applicationsCountResponse.data.applications?.count}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-t-gray-100 py-5 pl-[28px] pr-4 md:border-t-0 md:p-3 md:pb-6">
        {isMobile ? (
          <div className="flex flex-col p-5 gap-5 md:p-3 md:pb-4 md:gap-3 md:bg-white md:rounded-[14px]">
            <div className='flex flex-1 gap-[9px] items-center'>
              <Avatar
                user={currentUser}
                avatar={currentUser?.image}
                alt={`${currentUser?.firstName} ${currentUser?.lastName}`}
                height={32}
                width={32}
              />
              <p className="text-small text-black font-semibold">
                {currentUser?.firstName} {currentUser?.lastName}
              </p>
            </div>
            <div className="border-t border-t-gray-50 w-full" />
            <Button
              size="x-small"
              variant="nakedGrayLink"
              className="flex justify-between p-0"
              onClick={() => mustHaveProfile(() => router.push(userProfileLink))}
            >
              <p>Profile</p>
              <Button
                variant="nakedBrandLink"
                size="x-small"
                className="p-0"
                asChild
              >
                <AnimationAsyncButton
                  animationData={animationCopy}
                  onSuccess={() => {
                  }}
                  onClickAsyncAction={async () => {
                    mustHaveProfile(async () => await copy());
                    return true;
                  }}
                />
              </Button>
            </Button>
            <Button
              size="x-small"
              variant="nakedGrayLink"
              className="justify-start p-0"
              onClick={() => {
                window.open('https://linkedin.com/in/whyareureadingthis/', '_blank');
              }}
            >
              Give feedback
            </Button>
            {!isEnabledTWA && (
              <Button
                size="x-small"
                variant="nakedGrayLink"
                className="justify-start p-0"
                onClick={() => setShowLogoutPopup(() => true)}
              >
                Sign Out
              </Button>
            )}
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger className="py-1 pr-[11px] pl-1 w-full flex focus:outline-none items-center">
              <div className='flex flex-1 gap-[9px] items-center'>
                <Avatar
                  user={currentUser}
                  avatar={currentUser?.image}
                  alt={`${currentUser?.firstName} ${currentUser?.lastName}`}
                  height={32}
                  width={32}
                  defaultIconHeight={55}
                  defaultIconWidth={55}
                />
                <p className="text-small text-black font-semibold">
                  {currentUser?.firstName} {currentUser?.lastName}
                </p>
              </div>
              <div className="text-brand-500">
                <DotsIcon />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="p-[15px] rounded-[14px] border-0 gap-[10px] flex flex-col w-[236px]">
              <DropdownMenuItem
                className="flex justify-between"
                onClick={() => {
                  mustHaveProfile(() => {
                    router.push(userProfileLink);
                  });
                }}
              >
                <p>Profile</p>
                <Button
                  variant="nakedBrandLink"
                  size="x-small"
                  className="p-0"
                  asChild
                >
                  <AnimationAsyncButton
                    animationData={animationCopy}
                    onSuccess={() => {
                    }}
                    onClickAsyncAction={async () => {
                      mustHaveProfile(async () => await copy());
                      return true;
                    }}
                  />
                </Button>
              </DropdownMenuItem>
              {/*<DropdownMenuItem>Personal Settings</DropdownMenuItem>*/}
              {/*<DropdownMenuItem*/}
              {/*  onClick={() => {*/}
              {/*    window.open('https://app.gokyg.com/register', '_blank');*/}
              {/*  }}*/}
              {/*>Create Community</DropdownMenuItem>*/}
              <DropdownMenuItem
                onClick={() => {
                  window.open('https://linkedin.com/in/whyareureadingthis/', '_blank');
                }}
              >Give feedback</DropdownMenuItem>
              {!isEnabledTWA && (
                <DropdownMenuItem onClick={() => setShowLogoutPopup(() => true)}>
                  Sign Out
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <Dialog
        open={showLogoutPopup}
        onOpenChange={() => setShowLogoutPopup(() => false)}
      >
        <DialogContent className="p-0 sm-1/2:max-w-full sm-1/2:w-full">
          <div className="px-6 pb-6 pt-8 flex flex-col gap-8">
            <div className="flex flex-col flex-1 gap-4 items-center">
              <LogoutIcon height={48} width={48} className="text-error-600" />
              <p className="text-display-xs text-black font-semibold text-center">
                Are you sure you want <br />
                to Sign out?
              </p>
            </div>
            <div className="flex gap-[6px]">
              <Button
                type="button"
                variant="nakedGray"
                size="medium"
                className="flex-1"
                onClick={() => setShowLogoutPopup(() => false)}
              >
                Nevermind
              </Button>
              <Button
                type="button"
                variant="primaryBrand"
                size="medium"
                className="flex-1 bg-error-600 hover:bg-error-800 border-error-600 hover:border-error-800"
                onClick={handleLogout}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
